'use client';

import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store';
import { useCookieConsent } from '@/hooks/useCookieConsent';
import { saveCookieConsent } from '@/api/profile/cookieConsent';

const SYNCED_KEY = 'cookie_consent_synced';

type SyncedMarker = { userUuid: string; decidedAt: string };

const readSynced = (): SyncedMarker | null => {
  try {
    return JSON.parse(localStorage.getItem(SYNCED_KEY) || 'null');
  } catch {
    return null;
  }
};

/** Records the device's consent against the logged-in user once per decision. */
export const useCookieConsentSync = () => {
  const { consent } = useCookieConsent();
  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated,
  );
  const userUuid = useSelector((state: RootState) => state.auth.user?.uuid);

  useEffect(() => {
    if (!consent || !isAuthenticated || !userUuid) {
      return;
    }

    let hasToken = false;
    try {
      hasToken = !!localStorage.getItem('ACCESS_TOKEN');
    } catch {
      return;
    }
    if (!hasToken) {
      return;
    }

    const synced = readSynced();
    if (
      synced?.userUuid === userUuid &&
      synced?.decidedAt === consent.decidedAt
    ) {
      return;
    }

    saveCookieConsent(consent)
      .then(() => {
        try {
          localStorage.setItem(
            SYNCED_KEY,
            JSON.stringify({ userUuid, decidedAt: consent.decidedAt }),
          );
        } catch {
          // Unsynced marker just means a resend on the next load
        }
      })
      .catch(() => {
        // Not marked as synced, so it retries on the next load
      });
  }, [consent, isAuthenticated, userUuid]);
};
